import React from "react";
import { wire } from "react-hot-wire";
import { Field, FieldProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import classnames from "classnames";
import { WithTranslation, withTranslation } from "react-i18next";

import { SiteService } from "services/site/Site.service";

import { Application } from "models/application/Application";

import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";

import IconClose from "@material-ui/icons/Close";

import Drawer from "components/common/drawer/Drawer";

export type ImportApplicationFormValues = {
  sourceApplication: {
    siteName: string;
    applicationName: string;
  };
};

const ImportApplicationSchema = Yup.object().shape({
  sourceApplication: Yup.object().shape({
    siteName: Yup.string().required(),
    applicationName: Yup.string().required()
  })
});

function ImportApplication({
  siteService,
  application,
  drawerProps,
  action,
  t
}: {
  siteService: SiteService;
  application: Application;
  drawerProps?: any;
  action?: () => void;
} & WithTranslation) {
  return (
    <Drawer
      {...drawerProps}
      render={({ close }: { close: () => void }) => (
        <Formik
          initialValues={{
            sourceApplication: {
              siteName: "default",
              applicationName: ""
            }
          }}
          validationSchema={ImportApplicationSchema}
          onSubmit={(values: ImportApplicationFormValues) =>
            siteService
              .importApplicationData({
                values,
                application
              })
              .then(action)
              .then(close)
          }
          render={(props: FormikProps<ImportApplicationFormValues>) => {
            props.errors = Object.assign(
              { sourceApplication: {} },
              props.errors
            );
            props.touched = Object.assign(
              { sourceApplication: {} },
              props.touched
            );

            return (
              <Form className="d-flex flex-column h-100">
                <div className="d-flex flex-column h-100">
                  <div
                    className="d-flex p-3 justify-content-between align-items-center"
                    style={{ borderBottom: "1px solid #dadce0" }}
                  >
                    <IconButton onClick={close}>
                      <IconClose fontSize="small" />
                    </IconButton>
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      size="small"
                      className="p-2"
                      disabled={!props.isValid || props.isSubmitting}
                    >
                      {t("application.import.importButton")}
                    </Button>
                  </div>
                  <div
                    className="flex-grow-1 py-4 px-5 overflow-auto"
                    style={{ backgroundColor: "rgb(241, 243, 244)" }}
                  >
                    <section className="d-none box bg-white rounded mb-3">
                      <header className="px-4 py-3">
                        {t("application.import.fields.sourceSiteName.label")}
                      </header>
                      <section className="d-flex flex-column px-4 pb-4 pt-0">
                        <Field
                          name="sourceApplication[siteName]"
                          render={({
                            field
                          }: FieldProps<ImportApplicationFormValues>) => (
                            <TextField
                              {...field}
                              className={classnames("m-0", {
                                "is-valid":
                                  !props.errors.sourceApplication!.siteName &&
                                  props.touched.sourceApplication!.siteName,
                                "is-invalid":
                                  props.errors.sourceApplication!.siteName &&
                                  props.touched.sourceApplication!.siteName
                              })}
                              tabIndex={1}
                            />
                          )}
                        />
                        {props.errors.sourceApplication!.applicationName &&
                          props.touched.sourceApplication!.applicationName && (
                            <div className="d-flex invalid-feedback">
                              {t(
                                "application.import.fields.sourceSiteName.error"
                              )}
                            </div>
                          )}
                      </section>
                    </section>
                    <section className="box bg-white rounded mb-3">
                      <header className="px-4 py-3">
                        {t(
                          "application.import.fields.sourceApplicationName.label"
                        )}
                      </header>
                      <section className="d-flex flex-column px-4 pb-4 pt-0">
                        <Field
                          name="sourceApplication[applicationName]"
                          render={({
                            field
                          }: FieldProps<ImportApplicationFormValues>) => (
                            <TextField
                              {...field}
                              className={classnames("m-0", {
                                "is-valid":
                                  !props.errors.sourceApplication!
                                    .applicationName &&
                                  props.touched.sourceApplication!
                                    .applicationName,
                                "is-invalid":
                                  props.errors.sourceApplication!
                                    .applicationName &&
                                  props.touched.sourceApplication!
                                    .applicationName
                              })}
                              tabIndex={1}
                            />
                          )}
                        />
                        {props.errors.sourceApplication!.applicationName &&
                          props.touched.sourceApplication!.applicationName && (
                            <div className="d-flex invalid-feedback">
                              {t(
                                "application.import.fields.sourceApplicationName.error"
                              )}
                            </div>
                          )}
                      </section>
                    </section>
                  </div>
                </div>
              </Form>
            );
          }}
        />
      )}
    />
  );
}

export default wire(["siteService"], withTranslation()(ImportApplication));
